import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, Package, Heart, Search, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { AddressFields } from "./Checkout";

const EMPTY_ADDRESS = { name: "", phone: "", pin: "", city: "", state: "", locality: "", house: "", street: "", landmark: "", label: "Home" };

function AddressesTab({ user, setUser }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  const addresses = user.addresses || [];

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users/me/addresses", { ...form, phone: `91${form.phone.replace(/^91/, "")}` });
      toast.success("Address saved");
      setAdding(false);
      setForm(EMPTY_ADDRESS);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const remove = async (aid) => {
    await api.delete(`/users/me/addresses/${aid}`).catch((e) => toast.error(formatApiError(e)));
    toast("Address removed");
    refresh();
  };

  return (
    <div className="max-w-2xl">
      {addresses.length === 0 && !adding && (
        <div data-testid="addresses-empty" className="py-10 text-center border border-dashed border-vv-black/15">
          <p className="font-display text-2xl">No saved addresses yet.</p>
          <p className="text-sm text-vv-muted mt-2">Addresses used at checkout are saved here automatically.</p>
        </div>
      )}
      <div className="space-y-4">
        {addresses.map((a) => (
          <div key={a.id} data-testid={`address-card-${a.id}`} className="border border-vv-black/10 p-5 flex justify-between gap-4">
            <div className="text-sm leading-relaxed">
              <p className="font-medium flex items-center gap-2">
                <MapPin size={13} strokeWidth={1.5} className="text-vv-muted" /> {a.name}
                <span className="text-[9px] uppercase tracking-[0.2em] text-vv-muted border border-vv-black/15 px-2 py-0.5">{a.label}</span>
              </p>
              <p className="text-vv-muted mt-1.5">
                {a.house}, {a.street}{a.landmark ? `, near ${a.landmark}` : ""}, {a.city}, {a.state} — {a.pin}
              </p>
              <p className="text-vv-muted mt-0.5">+91 {(a.phone || "").replace(/^91/, "")}</p>
            </div>
            <button data-testid={`address-delete-${a.id}`} onClick={() => remove(a.id)} aria-label="Delete address"
              className="text-vv-muted hover:text-vv-burgundy transition-colors self-start">
              <Trash2 size={15} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
      {adding ? (
        <form data-testid="add-address-form" onSubmit={add} className="mt-8 border border-vv-black/10 p-6">
          <p className="vv-overline mb-6">New Address</p>
          <AddressFields address={form} setAddress={setForm} lockPhone={false} />
          <div className="flex gap-3 mt-6">
            <button data-testid="address-save-button" type="submit" disabled={busy} className="vv-btn-dark flex-1 disabled:opacity-50">
              {busy ? "Saving…" : "Save Address"}
            </button>
            <button type="button" data-testid="address-cancel-button" onClick={() => setAdding(false)} className="vv-btn-outline-dark">Cancel</button>
          </div>
        </form>
      ) : (
        addresses.length < 6 && (
          <button data-testid="add-address-button" onClick={() => setAdding(true)} className="vv-btn-outline-dark mt-6">
            Add New Address
          </button>
        )
      )}
    </div>
  );
}

export default function Account() {
  const { user, setUser, authChecked, wishlist } = useShop();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [trackForm, setTrackForm] = useState({ ref: "", identifier: "" });
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (authChecked && !user) navigate("/auth");
  }, [authChecked, user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    navigate("/");
  };

  const track = async (e) => {
    e.preventDefault();
    setTrackError(""); setTrackResult(null);
    try {
      const { data } = await api.get(`/orders/track/${trackForm.ref}?identifier=${encodeURIComponent(trackForm.identifier)}`);
      setTrackResult(data);
    } catch (err) {
      setTrackError(formatApiError(err));
    }
  };

  if (!user) return <main className="min-h-[50vh]" />;

  const tabs = [["orders", "Orders", Package], ["addresses", "Addresses", MapPin], ["wishlist", "Wishlist", Heart], ["track", "Track Order", Search]];
  const contact = user.email || (user.phone ? `+91 ${user.phone.replace(/^91/, "")}` : "");

  return (
    <main data-testid="account-page" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-12 md:py-20 min-h-screen">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="vv-overline text-vv-muted mb-4">My Account</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{user.name ? user.name.toUpperCase() : "MEMBER"}</h1>
          <p className="text-sm text-vv-muted mt-2">{contact}</p>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && <Link data-testid="account-admin-link" to="/admin" className="vv-btn-outline-dark">Admin Panel</Link>}
          <button data-testid="logout-button" onClick={logout} className="vv-btn-outline-dark">
            <LogOut size={12} /> Sign Out
          </button>
        </div>
      </Reveal>

      <div className="flex gap-8 border-b border-vv-black/10 mb-10 overflow-x-auto">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} data-testid={`account-tab-${key}`} onClick={() => setParams({ tab: key })}
            className={`flex items-center gap-2 pb-4 text-[11px] uppercase tracking-[0.25em] whitespace-nowrap transition-colors ${tab === key ? "border-b border-vv-black -mb-px font-medium" : "text-vv-muted"}`}>
            <Icon size={13} strokeWidth={1.5} /> {label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        orders.length === 0 ? (
          <div data-testid="orders-empty" className="py-16 text-center">
            <p className="font-display text-2xl">No orders yet.</p>
            <Link to="/shop" className="vv-btn-dark mt-6">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.ref}`} className="border border-vv-black/10 p-6">
                <div className="flex flex-wrap justify-between gap-4 mb-5">
                  <div>
                    <p className="font-mono text-sm">{o.ref}</p>
                    <p className="text-[11px] text-vv-muted mt-1">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
                  </div>
                  <div className="text-right">
                    <span data-testid={`order-status-${o.ref}`} className="text-[10px] uppercase tracking-[0.25em] px-3 py-1 bg-vv-forest text-vv-ivory">{o.status}</span>
                    <p className="text-sm font-medium mt-2">{inr(o.total)}</p>
                  </div>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {o.items.map((i, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-vv-elevated/50 pr-4">
                      <div className="w-12 h-14 bg-vv-elevated overflow-hidden">
                        {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <p className="text-xs font-medium">{i.name}</p>
                        <p className="text-[10px] text-vv-muted">{i.size} · ×{i.qty}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "addresses" && <AddressesTab user={user} setUser={setUser} />}

      {tab === "wishlist" && (
        wishlist.length === 0 ? (
          <div data-testid="wishlist-empty" className="py-16 text-center">
            <p className="font-display text-2xl">Nothing saved yet.</p>
            <Link to="/shop" className="vv-btn-dark mt-6">Browse the Collection</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8">
            {wishlist.map((p) => (
              <ProductCard key={p.id} product={{ ...p, images: [p.image], sizes: [], colors: [] }} />
            ))}
          </div>
        )
      )}

      {tab === "track" && (
        <div className="max-w-md">
          <p className="text-sm text-vv-muted mb-6">Enter your order reference (VV-XXXXXX) and the email or mobile number used at checkout.</p>
          <form onSubmit={track} className="space-y-5">
            <input data-testid="track-ref-input" required value={trackForm.ref} onChange={(e) => setTrackForm({ ...trackForm, ref: e.target.value })}
              placeholder="VV-XXXXXX" className="w-full bg-transparent border-b border-vv-black/25 focus:border-vv-black outline-none py-3 text-sm font-mono placeholder:text-vv-stone" />
            <input data-testid="track-identifier-input" required value={trackForm.identifier} onChange={(e) => setTrackForm({ ...trackForm, identifier: e.target.value })}
              placeholder="Email or mobile number" className="w-full bg-transparent border-b border-vv-black/25 focus:border-vv-black outline-none py-3 text-sm placeholder:text-vv-stone" />
            <button data-testid="track-submit-button" type="submit" className="vv-btn-dark">Track Order</button>
          </form>
          {trackError && <p data-testid="track-error" className="text-xs text-vv-burgundy mt-4">{trackError}</p>}
          {trackResult && (
            <div data-testid="track-result" className="mt-8 border border-vv-black/10 p-6">
              <div className="flex justify-between items-center">
                <p className="font-mono text-sm">{trackResult.ref}</p>
                <span className="text-[10px] uppercase tracking-[0.25em] px-3 py-1 bg-vv-forest text-vv-ivory">{trackResult.status}</span>
              </div>
              <p className="text-[11px] text-vv-muted mt-2">Placed {new Date(trackResult.created_at).toLocaleDateString("en-IN")} · {inr(trackResult.total)}</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
